import React, { useCallback, useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import { Close as CloseIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  DataGridPro,
  type GridColDef,
  type GridPaginationModel,
  type GridRenderCellParams,
} from '@mui/x-data-grid-pro';

import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { CLAIM_RECORD_STATUSES } from '~/consts/luxLoyalty';
import { ROLE_ADMIN_USER } from '~/consts/roles';

import { useListClaimRecords } from '~/hooks/apis/claimCampaigns';

import type {
  ClaimActionRecord,
  ClaimCampaign,
  ClaimRecord,
  ClaimRecordStatus,
  ListClaimRecordsParams,
} from '~/services/LuxLoyalty';

import SubmitClaimDialog from './SubmitClaimDialog';
import { getActionStatusColor } from './claimCampaignUtils';

interface ClaimRecordsDialogProps {
  open: boolean;
  campaign: ClaimCampaign | null;
  onClose: () => void;
}

const DEFAULT_PAGE_SIZE = 25;

/** Maps claim record status to a MUI Chip color */
function getClaimStatusColor(
  status: ClaimRecordStatus,
): 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary' {
  switch (status) {
    case 'COMPLETED':
      return 'success';
    case 'PARTIAL_FAILED':
      return 'warning';
    case 'FAILED':
      return 'error';
    case 'REJECTED':
      return 'error';
    case 'VALIDATED':
      return 'info';
    case 'PENDING':
      return 'default';
    default:
      return 'default';
  }
}

export default function ClaimRecordsDialog({ open, campaign, onClose }: ClaimRecordsDialogProps) {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [statusFilter, setStatusFilter] = useState<ClaimRecordStatus | ''>('');
  const [isSubmitClaimOpen, setIsSubmitClaimOpen] = useState(false);

  const queryParams: ListClaimRecordsParams = useMemo(
    () => ({
      status: statusFilter || undefined,
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize,
    }),
    [paginationModel, statusFilter],
  );

  const { data, isLoading } = useListClaimRecords(campaign?.id ?? '', queryParams, {
    enabled: open && !!campaign,
  });

  const claims: ClaimRecord[] = data?.result?.claims || [];
  const totalCount = data?.result?.pagination?.total ?? 0;

  const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) return prev;
      return model;
    });
  }, []);

  const columns: Array<GridColDef<ClaimRecord>> = useMemo(
    () => [
      { field: 'id', headerName: 'Claim ID', width: 300 },
      { field: 'customerId', headerName: 'Customer ID', width: 300 },
      { field: 'accountId', headerName: 'Account ID', width: 300 },
      {
        field: 'status',
        headerName: 'Status',
        width: 140,
        renderCell: ({ value }: GridRenderCellParams<ClaimRecord>) => (
          <Chip label={value} size="small" color={getClaimStatusColor(value as ClaimRecordStatus)} />
        ),
      },
      {
        field: 'claimedAt',
        headerName: 'Claimed At',
        width: 160,
        valueFormatter: (value: string | null) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'),
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        width: 160,
        valueFormatter: (value: string) => dayjs(value).format('DD/MM/YYYY HH:mm'),
      },
      {
        field: 'errorMessage',
        headerName: 'Error',
        width: 200,
        valueFormatter: (value: string | null) => value || '-',
      },
    ],
    [],
  );

  /**
   * Renders the detail panel for a claim record showing its actions.
   */
  const getDetailPanelContent = useCallback(
    ({ row }: { row: ClaimRecord }) => (
      <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Actions ({row.actions.length})
        </Typography>
        {row.actions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No actions recorded.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {row.actions.map((action: ClaimActionRecord) => (
              <Accordion key={action.id} disableGutters variant="outlined">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label={action.actionType} size="small" variant="outlined" />
                    <Chip label={action.status} size="small" color={getActionStatusColor(action.status)} />
                    {action.executedAt && (
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(action.executedAt).format('DD/MM/YYYY HH:mm:ss')}
                      </Typography>
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={0.5}>
                    {action.errorMessage && (
                      <Typography variant="body2" color="error">
                        <strong>Error:</strong> {action.errorMessage}
                      </Typography>
                    )}
                    {action.result && (
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          Result:
                        </Typography>
                        <Typography
                          variant="body2"
                          component="pre"
                          sx={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem', mt: 0.5 }}
                        >
                          {JSON.stringify(action.result, null, 2)}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        )}
      </Box>
    ),
    [],
  );

  const getDetailPanelHeight = useCallback(() => 'auto' as const, []);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Claims — {campaign?.name}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {/* Filter + Submit Claim button */}
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              select
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as ClaimRecordStatus | '');
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All</MenuItem>
              {CLAIM_RECORD_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ flex: 1 }} />
            <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER]}>
              <Button variant="contained" size="small" onClick={() => setIsSubmitClaimOpen(true)}>
                Submit Claim for User
              </Button>
            </PermissionedComponent>
          </Stack>

          {/* Data grid with expandable detail panel */}
          <Box sx={{ height: 550 }}>
            <DataGridPro
              rows={claims}
              columns={columns}
              loading={isLoading}
              pagination
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              pageSizeOptions={[10, 25, 50, 100]}
              rowCount={totalCount}
              getRowId={(row) => row.id}
              disableRowSelectionOnClick
              getDetailPanelContent={getDetailPanelContent}
              getDetailPanelHeight={getDetailPanelHeight}
              sx={{ height: '100%' }}
            />
          </Box>
        </Stack>
      </DialogContent>

      <SubmitClaimDialog open={isSubmitClaimOpen} campaign={campaign} onClose={() => setIsSubmitClaimOpen(false)} />
    </Dialog>
  );
}
